import { generateSite, PORT_BASTIA_SITE } from '@easy2log/simulation-engine';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  controlRun,
  createRun,
  deposerManifeste,
  enrollDevices,
  fetchLog,
  openStream,
  setAccessToken,
  type ApercuManifeste,
} from './api.js';
import { seConnecter, sessionEnregistree } from './auth.js';
import { Bulle } from './bulle.jsx';
import { BasculeLangue, Riche, useLocale, useT, useTErreur } from './i18n.jsx';
import { MapCanvas } from './map-canvas.jsx';
import type { BasemapMeta } from './geo-view.js';
import {
  CAS_METIER,
  REGLAGES_PAR_DEFAUT,
  avecTerrain,
  construireScenario,
  effectifsDuCas,
  reglagesDepuisCas,
  type Reglages,
} from './reglages.js';
import type { RunSnapshot, StateChangeEntry } from './types.js';
import { libelleRapprochement, rapprochementEnEchec } from './etat-rapprochement.js';

/**
 * Interface du simulateur.
 *
 * Elle ne simule rien : elle pilote un Durable Object et affiche ses
 * instantanés. C'est délibéré — la simulation doit survivre à la fermeture de
 * l'onglet, et deux personnes doivent pouvoir observer le même débarquement.
 */

const site = generateSite(PORT_BASTIA_SITE);
const CODES_DE_PLACES = site.slots.map((s) => s.code);

const SPEED_STEPS = [1, 2, 5, 10, 20] as const;

/**
 * Une escale disponible hors ligne, décrite par `public/plans/index.json`.
 *
 * Le fichier est ENGENDRÉ par `scripts/decouper-plans-chargement.mjs` à partir
 * d'un export du service fret. Rien n'est écrit en dur ici : déposer un nouvel
 * export remplace la liste, l'écran suit.
 */
interface PlanDisponible {
  fichier: string;
  depart: string | null;
  navire: string;
  traversee: string;
  unites: number;
}

/**
 * Garde de forme sur un fichier lu à l'exécution. Une entrée mal formée est
 * IGNORÉE plutôt que de faire tomber l'écran : le catalogue est une commodité,
 * pas une source — le dépôt de fichier reste toujours disponible à côté.
 */
function estPlanDisponible(valeur: unknown): valeur is PlanDisponible {
  if (typeof valeur !== 'object' || valeur === null) return false;
  const v = valeur as Record<string, unknown>;
  return (
    typeof v['fichier'] === 'string' &&
    typeof v['navire'] === 'string' &&
    typeof v['traversee'] === 'string' &&
    typeof v['unites'] === 'number'
  );
}

function formatSimTime(simAtMs: number): string {
  if (!Number.isFinite(simAtMs) || simAtMs === 0) return '—';
  return new Date(simAtMs).toISOString().replace('T', ' ').slice(0, 19);
}

function pourcent(valeur: number): string {
  return `${String(Math.round(valeur * 100))} %`;
}

/*
 * Les deux curseurs traduisent EUX-MÊMES leur libellé et leur aide, au lieu que
 * chacun des quarante appels le fasse : les textes restent écrits en clair au
 * point d'appel, lisibles, et la traduction se fait une fois pour toutes ici.
 */

/** Curseur de probabilité, gradué en pour-cent : personne ne règle un 0,08. */
function CurseurProbabilite({
  libelle,
  valeur,
  aide,
  onChange,
}: {
  libelle: string;
  valeur: number;
  aide?: string;
  onChange: (valeur: number) => void;
}): ReactElement {
  const t = useT();
  return (
    <label>
      {t(libelle)}
      {aide !== undefined && <Bulle>{t(aide)}</Bulle>}{' '}
      <span className="value">{pourcent(valeur)}</span>
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(valeur * 100)}
        onChange={(event): void => { onChange(Number(event.target.value) / 100); }}
      />
    </label>
  );
}

function CurseurValeur({
  libelle,
  valeur,
  min,
  max,
  pas = 1,
  unite,
  aide,
  onChange,
}: {
  libelle: string;
  valeur: number;
  min: number;
  max: number;
  pas?: number;
  unite: string;
  aide?: string;
  onChange: (valeur: number) => void;
}): ReactElement {
  const t = useT();
  return (
    <label>
      {t(libelle)}
      {aide !== undefined && <Bulle>{t(aide)}</Bulle>}{' '}
      <span className="value">
        {valeur} {t(unite)}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={pas}
        value={valeur}
        onChange={(event): void => { onChange(Number(event.target.value)); }}
      />
    </label>
  );
}

export function App(): JSX.Element {
  const t = useT();
  const tErreur = useTErreur();
  const locale = useLocale();
  const [meta, setMeta] = useState<BasemapMeta | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<RunSnapshot | null>(null);
  const [log, setLog] = useState<StateChangeEntry[]>([]);

  /*
   * « ok » et « déclenché » sont les deux seuls états sains. Tout le reste est
   * un motif d'échec, et doit se voir : c'est le silence de ce chemin qui a
   * laissé croire, le 25 août 2026, qu'un run ayant déposé 67 remorques en
   * avait consolidé 67, alors qu'il en avait consolidé 26.
   *
   * L'absence de statut n'est PAS un échec : aucun rapprochement n'a encore eu
   * lieu, ce qui est normal au début d'un run.
   */
  const matchingEnEchec = rapprochementEnEchec(snapshot?.matchingStatus);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [authentifie, setAuthentifie] = useState(false);
  const [connexionEnCours, setConnexionEnCours] = useState(false);
  const [enrolement, setEnrolement] = useState<string | null>(null);

  const [tractorCount, setTractorCount] = useState(3);
  const [trailerCount, setTrailerCount] = useState(20);
  /**
   * Manifeste déposé. Tant qu'il est là, il PRIME sur le curseur « Remorques » :
   * le débarquement porte sur les remorques du plan de chargement, dans son
   * ordre, et pas sur un effectif choisi au curseur.
   */
  const [manifeste, setManifeste] = useState<ApercuManifeste | null>(null);
  /**
   * Le fichier est CONSERVÉ après lecture : écrire en base ne doit pas obliger à
   * le redésigner. Et son nom reste affiché — le champ natif, lui, est remis à
   * zéro pour qu'un même fichier redéposé déclenche bien un nouveau `change`,
   * ce qui faisait croire que rien ne s'était passé.
   */
  const [fichierManifeste, setFichierManifeste] = useState<File | null>(null);
  const [manifesteEnCours, setManifesteEnCours] = useState(false);
  const [manifesteErreur, setManifesteErreur] = useState<string | null>(null);
  const [seed, setSeed] = useState(20260804);
  const [speedFactor, setSpeedFactor] = useState(5);

  const [reglages, setReglages] = useState<Reglages>(REGLAGES_PAR_DEFAUT);
  // Cas métier appliqué TEL QUEL : ses surcharges partent verbatim, y compris ce
  // que les curseurs ne modélisent pas (une seconde coupure ciblée, par exemple).
  // Toucher un curseur retombe en réglage manuel, et le dit.
  const [casActif, setCasActif] = useState<string | null>(null);
  const [menuCasOuvert, setMenuCasOuvert] = useState(false);
  const [plansDisponibles, setPlansDisponibles] = useState<PlanDisponible[]>([]);
  const [menuPlansOuvert, setMenuPlansOuvert] = useState(false);
  /**
   * COCHÉE par défaut, et c'est un renversement délibéré.
   *
   * C'est le seul chemin qui produit « confirmed ». Décochée, chaque run
   * plafonnait à « qr_only » sans que rien ne le laisse prévoir avant la fin —
   * un réglage par défaut qui rend le banc muet sur ce qu'il doit justement
   * démontrer. Le décocher reste possible, et c'est alors un choix.
   */
  const [sourceOperateur, setSourceOperateur] = useState(true);
  // Jamais persistée, jamais journalisée, vidée après création. Une clé saisie
  // ici ne doit pas survivre à la création du run.
  const [cleOperateur, setCleOperateur] = useState('');
  const [boitiersEnroles, setBoitiersEnroles] = useState(false);
  const [showSlotCodes, setShowSlotCodes] = useState(true);
  // Opaque par défaut : c'est la vue la plus nette du parking, et elle est là
  // pour ça. Le curseur permet de revenir à l'orthophoto pour comparer.
  const [surcoucheOpacite, setSurcoucheOpacite] = useState(1);

  // Une session encore valable évite de redemander les identifiants. Le jeton
  // est celui de l'API d'administration, qui relaie les commandes vers le
  // simulateur : le jeton d'administration du simulateur ne quitte jamais le
  // serveur.
  useEffect(() => {
    const session = sessionEnregistree();
    if (session === null) return;
    setAccessToken(session.accessToken);
    setAuthentifie(true);
  }, []);

  const handleSeConnecter = useCallback((): void => {
    setError(null);
    setConnexionEnCours(true);
    void seConnecter(email.trim(), motDePasse)
      .then((session) => {
        setAccessToken(session.accessToken);
        setAuthentifie(true);
        setMotDePasse('');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Connexion refusée.');
      })
      .finally(() => {
        setConnexionEnCours(false);
      });
  }, [email, motDePasse]);

  useEffect(() => {
    void fetch('/basemap/basemap.json')
      .then((response) => (response.ok ? (response.json() as Promise<BasemapMeta>) : null))
      .then(setMeta)
      .catch(() => { setMeta(null); });
  }, []);

  useEffect(() => {
    if (runId === null) return undefined;
    return openStream(runId, setSnapshot, setConnected);
  }, [runId]);

  // Le journal n'a pas besoin d'être en temps réel : il se consulte, il ne se
  // surveille pas. Un rafraîchissement toutes les cinq secondes suffit et
  // épargne des requêtes Durable Object.
  useEffect(() => {
    if (runId === null) return undefined;
    const refresh = (): void => {
      void fetchLog(runId).then(setLog);
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return (): void => { clearInterval(timer); };
  }, [runId]);

  const guard = useCallback(async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erreur inconnue');
    } finally {
      setBusy(false);
    }
  }, []);

  // Toute modification manuelle sort du cas métier : le bouton s'éteint, et ce
  // qui part est ce que montrent les curseurs.
  const modifier = useCallback((patch: Partial<Reglages>): void => {
    setCasActif(null);
    setReglages((precedent) => ({ ...precedent, ...patch }));
  }, []);

  const appliquerCas = useCallback((cle: string): void => {
    const cas = CAS_METIER.find((c) => c.cle === cle);
    if (cas === undefined) return;
    setCasActif(cle);
    setReglages(reglagesDepuisCas(cas));
    const effectifs = effectifsDuCas(cas);
    if (effectifs.tracteurs !== undefined) setTractorCount(effectifs.tracteurs);
    if (effectifs.remorques !== undefined) setTrailerCount(effectifs.remorques);
  }, []);

  /**
   * Dépôt d'un plan de chargement. Aperçu d'abord : rien n'est écrit tant que
   * l'utilisateur n'a pas vu quoi.
   *
   * L'erreur est tenue ICI plutôt que par `guard`, qui l'affiche en tête du
   * panneau — à plusieurs écrans du champ de fichier. Un échec y passait
   * inaperçu, et le champ paraissait simplement inerte.
   */
  const deposer = useCallback((fichier: File, ecrire: boolean): void => {
    setFichierManifeste(fichier);
    setManifesteErreur(null);
    setManifesteEnCours(true);
    void (async () => {
      try {
        setManifeste(await deposerManifeste(fichier, { ecrire }));
      } catch (cause) {
        setManifeste(null);
        setManifesteErreur(cause instanceof Error ? cause.message : 'Erreur inconnue');
      } finally {
        setManifesteEnCours(false);
      }
    })();
  }, []);

  /*
   * Catalogue des escales embarquées avec l'application. Son absence n'est PAS
   * une erreur : un dépôt fraîchement cloné n'a pas encore de plans découpés,
   * et le menu se contente alors de ne pas s'afficher.
   */
  useEffect(() => {
    let vivant = true;
    void fetch('plans/index.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((corps: unknown) => {
        if (!vivant || corps === null || typeof corps !== 'object') return;
        const liste = (corps as { plans?: unknown }).plans;
        if (!Array.isArray(liste)) return;
        setPlansDisponibles(liste.filter(estPlanDisponible));
      })
      .catch(() => {
        // Réseau ou fichier absent : le dépôt manuel reste disponible.
      });
    return (): void => {
      vivant = false;
    };
  }, []);

  /**
   * Ouvre une escale du catalogue.
   *
   * Le fichier est rechargé puis passé à `deposer`, exactement comme s'il avait
   * été choisi à la main : UN SEUL chemin d'import, donc un seul comportement à
   * vérifier. Un raccourci qui appellerait l'API directement se mettrait à
   * diverger du dépôt manuel sans que rien ne le signale.
   */
  const ouvrirEscale = useCallback(
    (plan: PlanDisponible): void => {
      setMenuPlansOuvert(false);
      setManifesteErreur(null);
      setManifesteEnCours(true);
      void (async () => {
        try {
          const reponse = await fetch(`plans/${plan.fichier}`);
          if (!reponse.ok) throw new Error(`Escale introuvable (${String(reponse.status)})`);
          const contenu = await reponse.blob();
          deposer(new File([contenu], plan.fichier, { type: 'text/csv' }), false);
        } catch (cause) {
          setManifesteEnCours(false);
          setManifesteErreur(cause instanceof Error ? cause.message : 'Erreur inconnue');
        }
      })();
    },
    [deposer],
  );

  const handleCreate = useCallback((): void => {
    void guard(async () => {
      const cas = casActif === null ? undefined : CAS_METIER.find((c) => c.cle === casActif);
      const base =
        cas === undefined
          ? construireScenario(reglages, tractorCount, trailerCount)
          : avecTerrain(cas.surcharges, construireScenario(reglages, tractorCount, trailerCount), {
              tractorCount,
              trailerCount,
            });

      // Le fragment vient APRÈS : il doit écraser `trailerCount`, pas l'inverse.
      // Le moteur ignore ce compte dès qu'un manifeste est présent, mais laisser
      // les deux se contredire dans la charge utile rendrait l'écran illisible
      // le jour où quelqu'un lit la requête pour comprendre un run.
      const scenario = manifeste === null ? base : { ...base, ...manifeste.scenario_fragment };

      const created = await createRun({
        seed,
        speedFactor,
        scenario,
        externalSource: sourceOperateur,
        ...(cleOperateur === '' ? {} : { externalApiKey: cleOperateur }),
      });
      // La clé ne survit pas à la création : elle n'a aucune raison de rester en
      // mémoire de la page une fois transmise.
      setCleOperateur('');
      setBoitiersEnroles(false);
      setRunId(created.run_id);
      setSnapshot(null);
      setLog([]);
    });
  }, [
    guard,
    seed,
    speedFactor,
    tractorCount,
    trailerCount,
    reglages,
    casActif,
    sourceOperateur,
    cleOperateur,
    manifeste,
  ]);

  const codesInstantane = snapshot?.occupiedSlotCodes;
  const occupiedSlotCodes = useMemo(() => {
    // L'instantané porte le parc au départ ET les poses : le journal ne voit que
    // les poses, et colorait vide un parc qui ne l'est pas.
    const codes = new Set<string>(codesInstantane ?? []);
    for (const entry of log) {
      const slot = entry.groundTruth['slot_code'];
      if (typeof slot === 'string' && entry.newState === 'posee') codes.add(slot);
    }
    return codes;
  }, [log, codesInstantane]);

  const status = snapshot?.status ?? 'created';
  const running = status === 'running';

  if (!authentifie) {
    return (
      <div className="login">
        <img
          className="bandeau"
          src="/bandeau-interreg-easy2log.png"
          alt={t('Interreg Marittimo Italie-France — cofinancé par l’Union européenne — EASY2LOG')}
        />
        <div className="login__entete">
          <h1>easy2log — Simulateur</h1>
          <BasculeLangue />
        </div>
        <p className="hint">
          {t(
            'Même compte que l’administration. Les commandes du simulateur passent par le serveur, qui seul détient le jeton du worker de simulation — la page n’en contient aucun.',
          )}
        </p>
        <input
          type="email"
          value={email}
          placeholder={t('Courriel')}
          autoComplete="username"
          onChange={(event): void => { setEmail(event.target.value); }}
        />
        <input
          type="password"
          value={motDePasse}
          placeholder={t('Mot de passe')}
          autoComplete="current-password"
          onChange={(event): void => { setMotDePasse(event.target.value); }}
          onKeyDown={(event): void => {
            if (event.key === 'Enter') handleSeConnecter();
          }}
        />
        <button type="button" onClick={handleSeConnecter} disabled={connexionEnCours}>
          {connexionEnCours ? t('Connexion…') : t('Se connecter')}
        </button>
        {error !== null && <p className="error">{tErreur(error)}</p>}
        {/*
          Sur l'écran de connexion AUSSI : c'est là qu'arrive quelqu'un qui ne
          connaît pas l'outil, et le mode d'emploi est précisément ce qu'il lui
          faut avant d'avoir un compte.
        */}
        <p className="login__manuel">
          <a
            className="lien-manuel"
            href="mode-emploi.html"
            target="_blank"
            rel="noopener"
            title={t('S’ouvre dans un nouvel onglet')}
          >
            {t('Mode d’emploi')}
          </a>
        </p>
        <div className="logos">
          <img
            src="/epcic.png"
            alt={t('EPCIC — Établissement public du commerce et de l’industrie de Corse')}
          />
          <img
            src="/unica.png"
            alt={t('UNICA — Università degli Studi di Cagliari, chef de file')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="layout">
      <aside className="panel">
        <img
          className="bandeau"
          src="/bandeau-interreg-easy2log.png"
          alt={t('Interreg Marittimo Italie-France — cofinancé par l’Union européenne — EASY2LOG')}
        />
        {/*
          Trois bandes : le bandeau du programme en haut, les partenaires
          en bas, et SEULE la bande du milieu défile. Le panneau est long
          — neuf sections de réglages — et une attribution de financement
          qui disparaît au premier coup de molette ne remplit pas son
          office.
        */}
        <div className="panel__defilant">
          <div className="panel__entete">
            <h1>{t('Simulateur de débarquement')}</h1>
            {/*
              Servi par l'application elle-même, et non par un lien extérieur :
              il s'ouvre pour quiconque atteint cet écran, sans compte tiers, et
              il part avec le déploiement.
            */}
            <a
              className="lien-manuel"
              href="mode-emploi.html"
              target="_blank"
              rel="noopener"
              title={t('S’ouvre dans un nouvel onglet')}
            >
              {t('Mode d’emploi')}
            </a>
            <BasculeLangue />
          </div>

          <section>
            <h2>{t('Scénario — étape 1 sur 3')}</h2>
            <label>
              {t('Tracteurs')} <span className="value">{tractorCount}</span>
              <input
                type="range"
                min={1}
                max={10}
                value={tractorCount}
                disabled={runId !== null}
                onChange={(event): void => { setTractorCount(Number(event.target.value)); }}
              />
            </label>
            <label>
              {t('Remorques')} <span className="value">{manifeste === null ? trailerCount : manifeste.scenario_fragment.manifestTrailers.length}</span>
              <input
                type="range"
                min={1}
                max={64}
                value={trailerCount}
                // Un manifeste fixe l'effectif : laisser le curseur actif
                // laisserait croire qu'il agit encore, alors que le moteur ignore
                // `trailerCount` dès qu'une liste est fournie.
                disabled={runId !== null || manifeste !== null}
                onChange={(event): void => { setTrailerCount(Number(event.target.value)); }}
              />
            </label>

            <fieldset className="manifeste">
              <legend>
                {t('Plan de chargement')}
                <Bulle>
                  <Riche cle="manifeste">
                    Le fichier du service fret — <code>.xls</code> ou <code>.csv</code>. Il remplace
                    les plaques fabriquées par les immatriculations réelles, dans l’ordre de
                    débarquement prédit.
                  </Riche>
                </Bulle>
              </legend>

              {/*
                Ouverture directe, AVANT le champ de fichier : c'est le geste
                courant — on éprouve une escale déjà découpée —, le dépôt manuel
                restant là pour un export qui vient d'arriver. Le menu disparaît
                si le catalogue est absent plutôt que de proposer du vide.
              */}
              {plansDisponibles.length > 0 && (
                <div className="menu">
                  <button
                    type="button"
                    className="menu__declencheur"
                    aria-expanded={menuPlansOuvert}
                    disabled={runId !== null || manifesteEnCours}
                    onClick={(): void => { setMenuPlansOuvert((ouvert) => !ouvert); }}
                  >
                    {t('Ouvrir une escale')}
                  </button>
                  {menuPlansOuvert && (
                    <ul className="menu__liste menu__liste--long">
                      {plansDisponibles.map((plan) => (
                        <li key={plan.fichier}>
                          <button type="button" onClick={(): void => { ouvrirEscale(plan); }}>
                            {t('{depart} — {navire} — {n} unités', {
                              depart: (plan.depart ?? plan.fichier).replace('T', ' à '),
                              navire: plan.navire,
                              n: plan.unites,
                            })}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <input
                type="file"
                accept=".xls,.csv,application/vnd.ms-excel,text/csv"
                disabled={runId !== null || manifesteEnCours}
                onChange={(event): void => {
                  const fichier = event.target.files?.[0];
                  if (fichier !== undefined) deposer(fichier, false);
                  // Le champ est remis à zéro pour que redéposer le MÊME fichier
                  // déclenche bien une nouvelle lecture. Le nom reste affiché
                  // au-dessous : sans ça, le champ paraît n'avoir rien fait.
                  event.target.value = '';
                }}
              />

              {fichierManifeste !== null && (
                <p className="manifeste__fichier">
                  {fichierManifeste.name} — {Math.round(fichierManifeste.size / 1024)} Ko
                  {manifesteEnCours && ` · ${t('lecture en cours…')}`}
                </p>
              )}

              {manifesteErreur !== null && (
                <p className="manifeste__erreur">
                  {t('Échec du dépôt :')} {manifesteErreur}
                </p>
              )}

              {manifeste !== null && (
                <div className="manifeste__apercu">
                  <p>
                    <strong>{manifeste.escale}</strong> —{' '}
                    {manifeste.entete.navire ?? t('navire inconnu')} ({manifeste.entete.origine} →{' '}
                    {manifeste.entete.destination})
                  </p>
                  <p>
                    {t('{retenues} remorques retenues sur {lues} unités lues', {
                      retenues: manifeste.unites_retenues,
                      lues: manifeste.unites_lues,
                    })}
                    {manifeste.sans_immatriculation > 0 &&
                      ` · ${t('{n} sans immatriculation', { n: manifeste.sans_immatriculation })}`}
                  </p>

                  {/*
                    La précision n'est pas un détail d'affichage : à la minute, une
                    partie du manifeste perd son rang de débarquement, et l'ordre
                    livré vaut moins que ce que l'écran laisse croire.
                  */}
                  {manifeste.precision_horaire !== 'seconde' && (
                    <p className="manifeste__alerte">
                      {/*
                        Phrase entière confiée à `t` plutôt que découpée autour des
                        <code> : l'italien ne place pas « à la minute » au même
                        endroit, et une phrase en morceaux ne se traduit pas.
                      */}
                      {t(
                        'Source {format}, horodatage à la {precision} : {n} remorques restent à égalité et n’ont pas de rang. Un fichier .xls les départage.',
                        {
                          format: manifeste.format,
                          precision: t(manifeste.precision_horaire),
                          n: manifeste.anomalies.filter((a) => a.code === 'ordre_indetermine').length,
                        },
                      )}
                    </p>
                  )}

                  {manifeste.ecrit ? (
                    <p className="manifeste__ecrit">
                      {t('Écrit en base : {n} remorques au référentiel.', {
                        n: manifeste.remorques_inscrites ?? 0,
                      })}
                    </p>
                  ) : (
                    <p className="manifeste__aide">
                      {t(
                        'Rien n’est encore écrit. Sans écriture, ces remorques seront inconnues du référentiel et le run ne produira que des provisoires.',
                      )}
                    </p>
                  )}

                  <div className="manifeste__actions">
                    {/* Le fichier est déjà en mémoire : écrire ne demande pas de
                        le redésigner, seulement de renvoyer le même octet pour
                        octet — c'est le serveur qui relit, jamais l'écran. */}
                    <button
                      type="button"
                      disabled={runId !== null || manifesteEnCours || manifeste.ecrit}
                      onClick={(): void => {
                        if (fichierManifeste !== null) deposer(fichierManifeste, true);
                      }}
                    >
                      {manifeste.ecrit ? t('Écrit en base') : t('Écrire en base')}
                    </button>
                    <button
                      type="button"
                      disabled={runId !== null || manifesteEnCours}
                      onClick={(): void => {
                        setManifeste(null);
                        setFichierManifeste(null);
                        setManifesteErreur(null);
                      }}
                    >
                      {t('Retirer le manifeste')}
                    </button>
                  </div>
                </div>
              )}
            </fieldset>
            <label>
              {t('Graine')}
              <Bulle>
                {t(
                  'À graine identique, la simulation produit exactement les mêmes événements. C’est ce qui rend un débarquement rejouable.',
                )}
              </Bulle>
              <input
                type="number"
                value={seed}
                disabled={runId !== null}
                onChange={(event): void => { setSeed(Number(event.target.value)); }}
              />
            </label>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={sourceOperateur}
                disabled={runId !== null}
                onChange={(event): void => { setSourceOperateur(event.target.checked); }}
              />
              {t('L’opérateur de la compagnie valide les sorties')}
            </label>
            {/*
              Reste en clair : cocher ou non décide de ce que le run peut prouver,
              la case est lue à la création et ne se rattrape pas ensuite. Une
              bulle se survole après coup — ici il est déjà trop tard.
            */}
            <p className="hint">
              <Riche cle="source-operateur">
                <strong>Seul chemin qui produit « confirmed »</strong>, et il se décide ici : la case
                est lue à la création du run.
              </Riche>
              <Bulle>
                {t(
                  'Sans elle, les sorties opérateur ne partent pas et la consolidation plafonne à « qr_only ». La clé est lue par le serveur : la page n’en contient aucune.',
                )}
              </Bulle>
            </p>
            <details>
              <summary>{t('Éprouver une autre clé')}</summary>
              <label>
                {t('Clé d’API de la source opérateur')}
                <input
                  type="password"
                  autoComplete="off"
                  value={cleOperateur}
                  disabled={runId !== null}
                  placeholder={t('laisser vide : le serveur utilise la sienne')}
                  onChange={(event): void => { setCleOperateur(event.target.value); }}
                />
              </label>
              <p className="hint">
                {t(
                  'Saisie de dépannage. Elle n’est ni conservée, ni journalisée, ni versionnée, et elle est effacée dès la création du run.',
                )}
              </p>
            </details>

            <button type="button" onClick={handleCreate} disabled={busy || runId !== null}>
              {t('Créer le run')}
            </button>
          </section>

          <section>
            <h2>
              {t('Cas métier')}
              <Bulle>
                {t(
                  'Un bouton applique un préréglage ENTIER, aligné sur le scénario du test correspondant.',
                )}
              </Bulle>
            </h2>
            {/*
              Menu rendu DANS la page, et non un <select> natif.

              Le <select> fonctionnait, mais sa fenêtre de choix est dessinée par
              le système : elle ne s'ouvre pas dans un navigateur embarqué, et
              l'écran paraissait cassé alors qu'il ne l'était pas. Ce menu-ci est
              du HTML ordinaire — il s'ouvre partout, et il parle la même langue
              visuelle que les autres blocs repliables du panneau.

              La référence du §19 est dans chaque entrée : c'est elle qu'on
              cherche quand on vient reproduire un scénario de test précis.
            */}
            <div className="menu">
              <button
                type="button"
                className="menu__declencheur"
                aria-expanded={menuCasOuvert}
                disabled={runId !== null}
                onClick={(): void => { setMenuCasOuvert((ouvert) => !ouvert); }}
              >
                {casActif === null
                  ? t('— Réglage manuel —')
                  : `${CAS_METIER.find((c) => c.cle === casActif)?.reference ?? ''} — ${t(
                      CAS_METIER.find((c) => c.cle === casActif)?.libelle ?? '',
                    )}`}
              </button>
              {menuCasOuvert && (
                <ul className="menu__liste">
                  {CAS_METIER.map((cas) => (
                    <li key={cas.cle}>
                      <button
                        type="button"
                        className={cas.cle === casActif ? 'active' : ''}
                        onClick={(): void => {
                          appliquerCas(cas.cle);
                          setMenuCasOuvert(false);
                        }}
                      >
                        {cas.reference} — {t(cas.libelle)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {casActif !== null && (
              <p className="hint">
                {CAS_METIER.find((c) => c.cle === casActif)?.reference} —{' '}
                {t(CAS_METIER.find((c) => c.cle === casActif)?.explication ?? '')}
              </p>
            )}
          </section>

          <section>
            {/*
              La section entière se replie : neuf groupes de curseurs, c'est
              le plus gros bloc du panneau, et on ne les règle pas à chaque
              run. Repliée par défaut — ouverte, elle reléguait le pilotage
              hors écran.
            */}
            <details className="section">
              <summary>{t('Réglages fins')}</summary>

              <details>
                <summary>{t('Lecture du QR à bord')}</summary>
                <CurseurProbabilite
                  libelle="Le QR est lu"
                  valeur={reglages.qrLu}
                  onChange={(v): void => { modifier({ qrLu: v }); }}
                />
                <CurseurProbabilite
                  libelle="Lecture erronée"
                  valeur={reglages.qrMalLu}
                  aide="Un caractère confondu (5↔6, 8↔0, 1↔7). La valeur lue ne désigne alors aucune remorque du référentiel."
                  onChange={(v): void => { modifier({ qrMalLu: v }); }}
                />
                <CurseurProbabilite
                  libelle="Lectures rapprochées en double"
                  valeur={reglages.qrDouble}
                  onChange={(v): void => { modifier({ qrDouble: v }); }}
                />
                <CurseurProbabilite
                  libelle="Lecture après l’attelage"
                  valeur={reglages.qrTardif}
                  onChange={(v): void => { modifier({ qrTardif: v }); }}
                />
                <CurseurProbabilite
                  libelle="Lecture anticipée de la remorque suivante"
                  valeur={reglages.qrAnticipe}
                  aide="Le tracteur scanne dans la cale la remorque qu’il ira chercher ensuite, 45 à 180 s d’avance. La lecture tombe alors dans deux fenêtres d’attelage voisines."
                  onChange={(v): void => { modifier({ qrAnticipe: v }); }}
                />
              </details>

              <details>
                <summary>{t('Saisie de l’opérateur')}</summary>
                <CurseurProbabilite
                  libelle="L’opérateur valide la sortie"
                  valeur={reglages.opValide}
                  onChange={(v): void => { modifier({ opValide: v }); }}
                />
                <CurseurProbabilite
                  libelle="Il saisit une AUTRE remorque"
                  valeur={reglages.opConflit}
                  aide="Les deux sources se contredisent : le moteur ne tranche jamais, il exige une revue humaine."
                  onChange={(v): void => { modifier({ opConflit: v }); }}
                />
                <CurseurProbabilite
                  libelle="Ponctuation différente"
                  valeur={reglages.opPonctuation}
                  aide="« AB 123 CD » pour « AB-123-CD ». Doit rester la même remorque."
                  onChange={(v): void => { modifier({ opPonctuation: v }); }}
                />
                <CurseurProbabilite
                  libelle="Sortie saisie deux fois"
                  valeur={reglages.opDoublon}
                  onChange={(v): void => { modifier({ opDoublon: v }); }}
                />
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={reglages.opPoste}
                    onChange={(event): void => { modifier({ opPoste: event.target.checked }); }}
                  />
                  La source indique le poste de contrôle
                </label>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={reglages.opRang}
                    onChange={(event): void => { modifier({ opRang: event.target.checked }); }}
                  />
                  La source transmet un rang de validation
                </label>
              </details>

              <details>
                <summary>{t('Horloge de la compagnie')}</summary>
                <CurseurValeur
                  libelle="Dérive constante"
                  valeur={reglages.opDeriveS}
                  min={-120}
                  max={120}
                  unite="s"
                  aide="Mesuré au banc : sans QR lu, au-delà de ±30 s les premières identifications FAUSSES apparaissent ; à ±40 s elles se comptent par dizaines et les justes tombent à zéro. Avec le QR lu, aucune dérive ne produit d’identification fausse — elle produit du conflit."
                  onChange={(v): void => { modifier({ opDeriveS: v }); }}
                />
                <CurseurValeur
                  libelle="Gigue de saisie"
                  valeur={reglages.opGigueS}
                  min={0}
                  max={60}
                  unite="s"
                  aide="Tirée à chaque sortie, elle se compense en moyenne — contrairement à la dérive."
                  onChange={(v): void => { modifier({ opGigueS: v }); }}
                />
              </details>

              <details>
                <summary>{t('Signal GNSS')}</summary>
                <CurseurValeur
                  libelle="Reprise à la sortie du navire"
                  valeur={reglages.gnssRepriseS}
                  min={0}
                  max={40}
                  unite="s"
                  onChange={(v): void => { modifier({ gnssRepriseS: v }); }}
                />
                <CurseurProbabilite
                  libelle="Échec de reprise"
                  valeur={reglages.gnssEchec}
                  aide="Le tracteur roule sans position jusqu’au cycle suivant."
                  onChange={(v): void => { modifier({ gnssEchec: v }); }}
                />
                <CurseurProbabilite
                  libelle="Précision dégradée"
                  valeur={reglages.gnssDegrade}
                  onChange={(v): void => { modifier({ gnssDegrade: v }); }}
                />
                <CurseurValeur
                  libelle="Précision annoncée quand elle est dégradée"
                  valeur={reglages.gnssPrecisionDegradeeM}
                  min={2}
                  max={40}
                  unite="m"
                  aide="Au-delà de 15 m, les points sont écartés du calcul de position stabilisée — et conservés avec leur motif de rejet."
                  onChange={(v): void => { modifier({ gnssPrecisionDegradeeM: v }); }}
                />
                <CurseurValeur
                  libelle="Variabilité de la précision"
                  valeur={reglages.gnssVariabilite}
                  min={0}
                  max={1.5}
                  pas={0.1}
                  unite=""
                  aide="0 : précision constante et bruit blanc, le modèle d’origine. Au-dessus, la précision bouge d’un point à l’autre et l’erreur garde sa direction une vingtaine de secondes, comme un vrai récepteur."
                  onChange={(v): void => { modifier({ gnssVariabilite: v }); }}
                />
              </details>

              <details>
                <summary>{t('Réseau 5G du port')}</summary>
                <CurseurValeur
                  libelle="Coupure — début"
                  valeur={reglages.reseauCoupureDebutS}
                  min={0}
                  max={1800}
                  pas={30}
                  unite="s"
                  onChange={(v): void => { modifier({ reseauCoupureDebutS: v }); }}
                />
                <CurseurValeur
                  libelle="Coupure — durée"
                  valeur={reglages.reseauCoupureDureeS}
                  min={0}
                  max={600}
                  pas={30}
                  unite="s"
                  aide="Une coupure ne perd rien : les mesures s’accumulent dans le tampon avec leur horodatage d’origine et repartent telles quelles."
                  onChange={(v): void => { modifier({ reseauCoupureDureeS: v }); }}
                />
                <CurseurProbabilite
                  libelle="Lot rejoué à l’identique"
                  valeur={reglages.reseauDoublon}
                  aide="Mêmes event_id : le serveur doit produire zéro ligne en double."
                  onChange={(v): void => { modifier({ reseauDoublon: v }); }}
                />
                <CurseurProbabilite
                  libelle="Lot reçu dans le désordre"
                  valeur={reglages.reseauDesordre}
                  onChange={(v): void => { modifier({ reseauDesordre: v }); }}
                />
              </details>

              <details>
                <summary>{t('Cadence du débarquement')}</summary>
                <CurseurValeur
                  libelle="Une remorque libérée toutes les"
                  valeur={reglages.cadenceS}
                  min={5}
                  max={180}
                  pas={5}
                  unite="s"
                  aide="La cadence ne déplace pas le seuil de dérive tolérable, elle abaisse le rendement : à une remorque toutes les 25 s, plusieurs tombent dans la même fenêtre et le moteur refuse d’arbitrer."
                  onChange={(v): void => { modifier({ cadenceS: v }); }}
                />
              </details>

              <details>
                <summary>{t('Aléas du terrain')}</summary>
                <CurseurProbabilite
                  libelle="Remorques hors référentiel"
                  valeur={reglages.horsReferentiel}
                  aide="Elles ne portent aucun QR : rien à lire. Seul l’opérateur peut les identifier."
                  onChange={(v): void => { modifier({ horsReferentiel: v }); }}
                />
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={reglages.occupationAuHasard}
                    onChange={(event): void => { modifier({ occupationAuHasard: event.target.checked }); }}
                  />
                  {t('Occupation du parc tirée au hasard à chaque run, entre 5 et 35 %')}
                </label>
                {!reglages.occupationAuHasard && (
                <CurseurProbabilite
                  libelle="Parc déjà occupé à l’arrivée"
                  valeur={reglages.occupationParc}
                  aide="Remorques des escales précédentes, sur des places tirées au hasard. Le moteur laisse toujours assez de places libres pour le navire. Il ne lève AUCUNE anomalie : l’anomalie « slot_already_occupied » du §19.18 vient des options de RAPPROCHEMENT, toujours vides en production."
                  onChange={(v): void => { modifier({ occupationParc: v }); }}
                />
                )}
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={reglages.placesAuHasard}
                    onChange={(event): void => { modifier({ placesAuHasard: event.target.checked }); }}
                  />
                  {t('Le chauffeur prend une place libre au hasard, sans la poser au centre')}
                </label>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={reglages.tracteurEnPanne}
                    onChange={(event): void => { modifier({ tracteurEnPanne: event.target.checked }); }}
                  />
                  Le dernier tracteur tombe en panne après 2 min 30
                </label>
              </details>

              <details>
                <summary>{t('Horloge des boîtiers')}</summary>
                <CurseurValeur
                  libelle="Dérive de TRACTOR-01"
                  valeur={reglages.deriveBoitierS}
                  min={-120}
                  max={120}
                  unite="s"
                  aide="Appliquée à la MESURE, comme le ferait un boîtier dont l’horloge est fausse. Un seul boîtier dérive : décaler les trois reviendrait à décaler l’horloge du port."
                  onChange={(v): void => { modifier({ deriveBoitierS: v }); }}
                />
              </details>

              <p className="hint">
                {t('Seize des vingt-deux cas du §19 s’obtiennent ici. Cinq exigent un levier ailleurs.')}
                <Bulle>
                  <Riche cle="cas-hors-portee">
                    <strong>§19.17</strong> deux variantes du même identifiant — le test injecte une
                    seconde lecture dans le lot. <strong>§19.18</strong> place déjà occupée —
                    l’anomalie vient des options de rapprochement, pas du curseur ci-dessus.{' '}
                    <strong>§19.20</strong> dépose sans position fiable — les observations sont
                    dégradées après la simulation. <strong>§19.21</strong> correction manuelle —
                    c’est un geste d’administration. <strong>§19.22</strong> recalcul après changement
                    des règles — ce sont les options du rapprochement qui changent, pas le scénario.
                  </Riche>
                </Bulle>
              </p>
            </details>
          </section>

          {runId !== null && (
            <section>
              <h2>{t('Boîtiers — étape 2 sur 3')}</h2>
              <p className="hint">
                <Riche cle="enroler-avant">
                  <strong>Enrôler AVANT de démarrer</strong>, sinon le début du débarquement part en
                  réessais. Les clés existent dès la création du run.
                </Riche>
                <Bulle>
                  {t(
                    'Le simulateur emprunte le même chemin qu’un boîtier réel, contrôle d’identité compris. Tant que ses clés ne sont pas déclarées au registre, ses trames sont refusées et rien n’arrive en base.',
                  )}
                </Bulle>
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={(): void => {
                  void guard(async () => {
                    const bilan = await enrollDevices(runId);
                    setBoitiersEnroles(bilan.enrolled.length > 0);
                    setEnrolement(
                      bilan.enrolled.length === 0
                        ? t('Aucune clé trouvée — le run n’a pas été créé correctement.')
                        : t(
                            '{n} boîtier(s) enrôlé(s) : {liste}. Compter environ une minute de propagation avant de démarrer.',
                            { n: bilan.enrolled.length, liste: bilan.enrolled.join(', ') },
                          ),
                    );
                  });
                }}
              >
                {t('Enrôler les boîtiers')}
              </button>
              {enrolement !== null && <p className="hint">{enrolement}</p>}
              <p className="hint">
                <Riche cle="un-seul-run">
                  <strong>Un seul run enrôlé à la fois.</strong> Enrôler un second run rend les
                  trames du premier « signature_invalid ».
                </Riche>
                <Bulle>
                  <Riche cle="registre-boitiers">
                    Le registre est indexé par identifiant de boîtier — <code>device:TRACTOR-01</code>{' '}
                    à <code>TRACTOR-03</code> — et tous les runs utilisent les mêmes. Ce n’est donc
                    pas une panne : c’est la clé qui a changé.
                  </Riche>
                </Bulle>
              </p>
            </section>
          )}

          {runId !== null && (
            <section>
              <h2>{t('Pilotage — étape 3 sur 3')}</h2>
              {!boitiersEnroles && status === 'created' && (
                <p className="hint">
                  {t(
                    'Démarrer avant l’enrôlement fait refuser toutes les trames. Elles sont désormais conservées et réessayées, mais dans la limite de trois tentatives : enrôler d’abord reste la bonne séquence.',
                  )}
                </p>
              )}
              <div className="buttons">
                <button
                  type="button"
                  disabled={busy || running || (!boitiersEnroles && status === 'created')}
                  onClick={(): void => {
                    void guard(() =>
                      controlRun(runId, status === 'paused' ? 'resume' : 'start', speedFactor),
                    );
                  }}
                >
                  {status === 'paused' ? t('Reprendre') : t('Démarrer')}
                </button>
                <button
                  type="button"
                  disabled={busy || !running}
                  onClick={(): void => {
                    void guard(() => controlRun(runId, 'pause'));
                  }}
                >
                  {t('Pause')}
                </button>
                <button
                  type="button"
                  disabled={busy || status === 'stopped' || status === 'completed'}
                  onClick={(): void => {
                    void guard(() => controlRun(runId, 'stop'));
                  }}
                >
                  {t('Arrêter')}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={(): void => {
                    void guard(() => controlRun(runId, 'reset'));
                  }}
                >
                  {t('Remise à zéro')}
                </button>
              </div>

              <label>
                {t('Vitesse')}
                <Bulle>
                  {t(
                    'Le moteur découpe chaque tick en pas fixes d’une seconde : à graine égale, ×1 et ×20 produisent exactement les mêmes mesures, aux mêmes horodatages. Accélérer ne change que le temps réel écoulé.',
                  )}
                </Bulle>{' '}
                <span className="value">×{speedFactor}</span>
              </label>
              <div className="buttons">
                {SPEED_STEPS.map((step) => (
                  <button
                    key={step}
                    type="button"
                    className={step === speedFactor ? 'active' : ''}
                    disabled={busy}
                    onClick={(): void => {
                      setSpeedFactor(step);
                      void guard(() => controlRun(runId, 'speed', step));
                    }}
                  >
                    ×{step}
                  </button>
                ))}
              </div>
            </section>
          )}

          {snapshot !== null && (
            <section>
              <h2>{t('État')}</h2>
              <dl className="stats">
                <dt>{t('Statut')}</dt>
                <dd className={`status status-${snapshot.status}`}>{snapshot.status}</dd>
                <dt>{t('Flux')}</dt>
                <dd>{connected ? 'connecté' : 'reconnexion…'}</dd>
                <dt>{t('Heure simulée')}</dt>
                <dd>{formatSimTime(snapshot.simAtMs)}</dd>
                <dt>{t('Événements produits')}</dt>
                <dd>{(snapshot.eventsProduced ?? 0).toLocaleString(locale)}</dd>
                <dt>{t('Acceptés par le serveur')}</dt>
                <dd className={snapshot.eventsEmitted === 0 ? 'alert' : undefined}>
                  {snapshot.eventsEmitted.toLocaleString(locale)}
                </dd>
                <dt>{t('Rejetés')}</dt>
                <dd className={(snapshot.eventsRejected ?? 0) > 0 ? 'alert' : undefined}>
                  {(snapshot.eventsRejected ?? 0).toLocaleString(locale)}
                </dd>
                <dt>{t('Sorties opérateur')}</dt>
                <dd
                  className={
                    (snapshot.externalRejected ?? 0) > 0 || (snapshot.externalSkipped ?? 0) > 0
                      ? 'alert'
                      : undefined
                  }
                >
                  {t('{acceptees} acceptées, {rejetees} rejetées', {
                    acceptees: (snapshot.externalEmitted ?? 0).toLocaleString(locale),
                    rejetees: (snapshot.externalRejected ?? 0).toLocaleString(locale),
                  })}
                  {(snapshot.externalSkipped ?? 0) > 0 &&
                    `, ${t('{n} non envoyées', { n: (snapshot.externalSkipped ?? 0).toLocaleString(locale) })}`}
                </dd>
                <dt>{t('Lots envoyés')}</dt>
                <dd>{snapshot.batchesSent.toLocaleString(locale)}</dd>
                <dt>{t('Places occupées')}</dt>
                <dd>
                  {snapshot.occupiedSlots} / {snapshot.totalSlots}
                </dd>
                <dt>{t('Restant à débarquer')}</dt>
                <dd>{snapshot.trailersRemaining}</dd>
                <dt>{t('Rapprochement')}</dt>
                <dd className={matchingEnEchec ? 'alert' : undefined}>
                  {t(libelleRapprochement(snapshot.matchingStatus))}
                </dd>
              </dl>
              {matchingEnEchec && (
                <p className="hint">
                  <Riche cle="consolidation-echec">
                    <strong>La consolidation n’a pas abouti.</strong> Le débarquement peut être
                    terminé sans que les mouvements soient en base : ce que l’API de sortie livre à
                    UNICA reste alors la photographie précédente, sans que rien ne le dise. Motif
                    ci-dessus.
                  </Riche>
                  <Bulle>
                    {t(
                      '« déclenché » n’est pas un échec : la consolidation a été acceptée et se poursuit en arrière-plan. C’est le mode normal, et elle dure environ une minute.',
                    )}
                  </Bulle>
                </p>
              )}
              {(snapshot.eventsRejected ?? 0) > 0 && (
                <p className="hint">
                  {t(
                    'Des trames ont été refusées. L’historique en donne le statut et le corps d’erreur, ligne « emission_rejected ». Cause la plus fréquente : boîtiers non enrôlés, ou propagation du registre encore en cours.',
                  )}
                </p>
              )}
              {(snapshot.externalRejected ?? 0) > 0 && (
                <p className="hint">
                  {t(
                    'Des sorties opérateur ont été refusées — ligne « EXTERNAL-APP » de l’historique. Ce chemin s’authentifie par clé partagée : clé absente du worker, périmée, ou posée avec un saut de ligne.',
                  )}
                </p>
              )}
              {/*
                Deux causes, deux messages. Un seul message accusait le secret du
                serveur dans les deux cas, et envoyait chercher une panne qui
                n'existait pas quand la case n'avait tout simplement pas été cochée.
              */}
              {(snapshot.externalSkipped ?? 0) > 0 && snapshot.serverKeyAvailable === true && (
                <p className="error">
                  <strong>{t('Ce run a été créé sans source opérateur.')}</strong>{' '}
                  {t(
                    'Le moteur a produit {n} sortie(s) qui ne sont JAMAIS parties, et la consolidation ne pourra pas conclure « confirmed ». Le serveur détient bien la clé : il n’y a rien à déployer. Recréer le run avec la case « L’opérateur de la compagnie valide les sorties » cochée — elle est lue à la création et ne se rattrape pas en cours de route.',
                    { n: (snapshot.externalSkipped ?? 0).toLocaleString(locale) },
                  )}
                </p>
              )}
              {(snapshot.externalSkipped ?? 0) > 0 && snapshot.serverKeyAvailable !== true && (
                <p className="error">
                  <strong>{t('Aucune clé de source opérateur sur le serveur.')}</strong>{' '}
                  {t(
                    'Le moteur a produit {n} sortie(s) qui ne sont JAMAIS parties : la consolidation ne pourra pas conclure « confirmed ». Poser le secret SIMULATION_EXTERNAL_API_KEY sur le worker de simulation (wrangler secret bulk, jamais secret put), ou saisir une clé dans « Éprouver une autre clé » à la création du run.',
                    { n: (snapshot.externalSkipped ?? 0).toLocaleString(locale) },
                  )}
                </p>
              )}
              {snapshot.externalKeyPresent === false && (snapshot.externalSkipped ?? 0) === 0 && (
                <p className="hint">
                  <Riche cle="run-sans-operateur">
                    Ce run tourne <strong>sans</strong> source opérateur : aucune clé n’a été fournie
                    et le serveur n’en détient pas. L’identification plafonnera à « qr_only ».
                  </Riche>
                </p>
              )}
            </section>
          )}

          {snapshot !== null && (
            <section>
              <h2>{t('Tracteurs')}</h2>
              <table className="tractors">
                <tbody>
                  {snapshot.tractors.map((tractor) => (
                    <tr key={tractor.reference}>
                      <td>{tractor.reference.replace('TRACTOR-', 'T')}</td>
                      <td className="state">{tractor.state}</td>
                      <td>{tractor.trailerRef ?? '—'}</td>
                      <td className={tractor.gnssValid ? 'ok' : 'muted'}>
                        {tractor.gnssValid ? 'GNSS' : t('sans fix')}
                      </td>
                      <td className={tractor.networkUp ? 'ok' : 'alert'}>
                        {tractor.networkUp ? '5G' : t('hors ligne ({n})', { n: tractor.bufferedEvents })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <section>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={showSlotCodes}
                onChange={(event): void => { setShowSlotCodes(event.target.checked); }}
              />
              {t('Afficher les codes de place')}
            </label>

            <label>
              {t('Vue aérienne de lecture — {n} %', { n: Math.round(surcoucheOpacite * 100) })}
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(surcoucheOpacite * 100)}
                onChange={(event): void => { setSurcoucheOpacite(Number(event.target.value) / 100); }}
              />
            </label>
          </section>

          {error !== null && <p className="error">{tErreur(error)}</p>}
        </div>
        <div className="logos">
          <img
            src="/epcic.png"
            alt={t('EPCIC — Établissement public du commerce et de l’industrie de Corse')}
          />
          <img
            src="/unica.png"
            alt={t('UNICA — Università degli Studi di Cagliari, chef de file')}
          />
        </div>
      </aside>

      <main className="stage">
        {meta === null ? (
          <div className="placeholder">
            {/*
              Enveloppé dans un <p> : `.placeholder` est une grille centrée, et
              chaque enfant direct y devient une LIGNE. Le texte et le <code>
              s'empilaient donc sur trois lignes séparées par toute la hauteur
              de la carte. Le défaut précède le bilingue ; il s'est seulement vu
              en le relisant.
            */}
            <p>
              <Riche cle="fond-de-plan">
                Fond de plan indisponible. Exécuter <code>node scripts/prepare-basemap.mjs</code>.
              </Riche>
            </p>
          </div>
        ) : (
          <MapCanvas
            meta={meta}
            site={site}
            tractors={snapshot?.tractors ?? []}
            occupiedSlotCodes={occupiedSlotCodes}
            showSlotCodes={showSlotCodes}
            surcoucheOpacite={surcoucheOpacite}
          />
        )}

        <section className="log">
          <h2>{t('Historique')}</h2>
          <ul>
            {log.slice(0, 60).map((entry, index) => (
              <li key={`${String(entry.simAtMs)}-${entry.entityRef}-${String(index)}`}>
                <span className="time">{formatSimTime(entry.simAtMs).slice(11)}</span>
                <span className="ref">{entry.entityRef}</span>
                <span className="transition">
                  {entry.previousState ?? '—'} → <strong>{entry.newState}</strong>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}
